import {createApi} from '@reduxjs/toolkit/dist/query/react';
import {BigNumber} from 'ethers';
import {GFTRoot} from '../typechain-types';
import {contractBaseQuery} from './contract.base-query';
import {EApiSourceName} from './types';

const gftRootApi = createApi({
  reducerPath: 'gftRootApi',
  baseQuery: contractBaseQuery,
  tagTypes: ['User', 'Projects'],
  endpoints: builder => ({
    invest: builder.mutation<Awaited<ReturnType<GFTRoot['invest']>>, Parameters<GFTRoot['invest']>>({
      query: ([investmentId, projectId, ...args]) => ({
        contractName: EApiSourceName.GFT_ROOT,
        method: 'invest',
        callData: [`0x${investmentId}`, `0x${projectId}`, ...args],
      }),
    }),
    createProjectByAdmin: builder.mutation<
      Awaited<ReturnType<GFTRoot['createProject(uint256,bool)']>>,
      Parameters<GFTRoot['createProject(uint256,bool)']>
    >({
      query: ([projectId, ...args]) => ({
        contractName: EApiSourceName.GFT_ROOT,
        method: 'createProject(uint256,bool)',
        callData: [`0x${projectId}`, ...args],
      }),
    }),
    createProject: builder.mutation<
      Awaited<ReturnType<GFTRoot['createProject(uint256,bool,address)']>>,
      Parameters<GFTRoot['createProject(uint256,bool,address)']>
    >({
      query: ([projectId, ...args]) => ({
        contractName: EApiSourceName.GFT_ROOT,
        method: 'createProject(uint256,bool,address)',
        callData: [`0x${projectId}`, ...args],
      }),
    }),
    withdrawProject: builder.mutation<
      Awaited<ReturnType<GFTRoot['withdrawProject']>>,
      Parameters<GFTRoot['withdrawProject']>
    >({
      query: ([projectId, ...args]) => ({
        contractName: EApiSourceName.GFT_ROOT,
        method: 'withdrawProject',
        callData: [`${projectId}`, ...args],
      }),
    }),
    withdrawFee: builder.mutation<Awaited<ReturnType<GFTRoot['withdrawFee']>>, Parameters<GFTRoot['withdrawFee']>>({
      query: args => ({
        contractName: EApiSourceName.GFT_ROOT,
        method: 'withdrawFee',
        callData: args,
      }),
    }),
    getTotalRaised: builder.query<{[key in string]: BigNumber}, Parameters<GFTRoot['getTotalRaised']>>({
      query: ([projectId, ...args]) => ({
        contractName: EApiSourceName.GFT_ROOT,
        method: 'getTotalRaised',
        callData: [`0x${projectId}`, ...args],
      }),
      transformResponse: (baseQueryReturnValue, meta, arg) => {
        return (baseQueryReturnValue as BigNumber[]).reduce((acc, el, i) => {
          const tokenAddress = arg[1][i] as string;
          acc[tokenAddress] = el;
          return acc;
        }, {} as {[key in string]: BigNumber});
      },
      providesTags: ['User'],
    }),
    getTotalFees: builder.query<{[key in string]: BigNumber}, Parameters<GFTRoot['getTotalFee']>>({
      query: ([projectId, ...args]) => ({
        contractName: EApiSourceName.GFT_ROOT,
        method: 'getTotalFee',
        callData: [`0x${projectId}`, ...args],
      }),
      transformResponse: (baseQueryReturnValue, meta, arg) => {
        return (baseQueryReturnValue as BigNumber[]).reduce((acc, el, i) => {
          const tokenAddress = arg[1][i] as string;
          acc[tokenAddress] = el;
          return acc;
        }, {} as {[key in string]: BigNumber});
      },
      providesTags: ['User'],
    }),
    getAvailableToWithdraw: builder.query<
      {[key in string]: BigNumber},
      Parameters<GFTRoot['getAvailableForWithdrawal']>
    >({
      query: ([projectId, ...args]) => ({
        contractName: EApiSourceName.GFT_ROOT,
        method: 'getAvailableForWithdrawal',
        callData: [`0x${projectId}`, ...args],
      }),
      transformResponse: (baseQueryReturnValue, meta, arg) => {
        return (baseQueryReturnValue as BigNumber[]).reduce((acc, el, i) => {
          const tokenAddress = arg[1][i] as string;
          acc[tokenAddress] = el;
          return acc;
        }, {} as {[key in string]: BigNumber});
      },
      providesTags: ['User'],
    }),
    getAvailableFee: builder.query<
      Awaited<ReturnType<GFTRoot['getAvailableFee']>>,
      Parameters<GFTRoot['getAvailableFee']>
    >({
      query: args => ({
        contractName: EApiSourceName.GFT_ROOT,
        method: 'getAvailableFee',
        callData: args,
      }),
      providesTags: ['User'],
    }),
    setFee: builder.mutation<
      Awaited<ReturnType<GFTRoot['setProjectOwnerFee']>>,
      Parameters<GFTRoot['setProjectOwnerFee']>
    >({
      query: args => ({
        contractName: EApiSourceName.GFT_ROOT,
        method: 'setProjectOwnerFee',
        callData: args,
      }),
    }),
    setBaseFee: builder.mutation<Awaited<ReturnType<GFTRoot['setBaseFee']>>, Parameters<GFTRoot['setBaseFee']>>({
      query: args => ({
        contractName: EApiSourceName.GFT_ROOT,
        method: 'setBaseFee',
        callData: args,
      }),
    }),
    pause: builder.mutation<Awaited<ReturnType<GFTRoot['pause']>>, Parameters<GFTRoot['pause']>>({
      query: ([projectId, ...args]) => ({
        contractName: EApiSourceName.GFT_ROOT,
        method: 'pause',
        callData: [`0x${projectId}`, ...args],
      }),
    }),
    unpause: builder.mutation<Awaited<ReturnType<GFTRoot['unpause']>>, Parameters<GFTRoot['unpause']>>({
      query: ([projectId, ...args]) => ({
        contractName: EApiSourceName.GFT_ROOT,
        method: 'unpause',
        callData: [`0x${projectId}`, ...args],
      }),
    }),
    getFeeDecimals: builder.query<
      Awaited<ReturnType<GFTRoot['PERCENT_DECIMALS']>>,
      Parameters<GFTRoot['PERCENT_DECIMALS']>
    >({
      query: args => ({
        contractName: EApiSourceName.GFT_ROOT,
        method: 'PERCENT_DECIMALS',
        callData: args,
      }),
      providesTags: ['User'],
    }),
    getIsActive: builder.query<Awaited<ReturnType<GFTRoot['getIsActive']>>, Parameters<GFTRoot['getIsActive']>>({
      query: ([projectId, ...args]) => ({
        contractName: EApiSourceName.GFT_ROOT,
        method: 'getIsActive',
        callData: [`0x${projectId}`, ...args],
      }),
      providesTags: ['User'],
    }),
  }),
});

export const {
  useCreateProjectMutation,
  useSetBaseFeeMutation,
  useInvestMutation,
  useSetFeeMutation,
  useGetFeeDecimalsQuery,
  useWithdrawFeeMutation,
  useGetAvailableToWithdrawQuery,
  useGetTotalRaisedQuery,
  useGetTotalFeesQuery,
  useGetIsActiveQuery,
  useWithdrawProjectMutation,
  usePauseMutation,
  useUnpauseMutation,
  useCreateProjectByAdminMutation,
} = gftRootApi;

export default gftRootApi;
